import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  useIsMobile,
} from '@design-system';

import { Mail } from 'lucide-react';

import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useInvitePartner } from '@/features/add-partner';

import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

const invitePartnerSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Please enter a valid email address'),
});

type InvitePartnerFormValues = z.infer<typeof invitePartnerSchema>;

interface InvitePartnerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function InvitePartnerDialog({ open, onOpenChange, onSuccess }: InvitePartnerDialogProps) {
  const isMobile = useIsMobile();
  const { invitePartner, isPending, reset } = useInvitePartner({
    onSuccess: () => {
      onOpenChange(false);
      onSuccess?.();
    },
  });

  const form = useForm<InvitePartnerFormValues>({
    resolver: zodResolver(invitePartnerSchema),
    mode: 'onChange',
    defaultValues: { email: '' },
  });

  useEffect(() => {
    if (open) {
      form.reset();
      reset();
    }
  }, [open]);

  function handleSubmit(data: InvitePartnerFormValues) {
    invitePartner(data.email);
  }

  function handleOpenChange(isOpen: boolean) {
    if (!isOpen) {
      form.reset();
      reset();
    }
    onOpenChange(isOpen);
  }

  const title = 'Invite Partner';
  const description =
    "Enter your partner's email address. If they have an existing account, they'll receive an invitation to link your plans.";

  const formContent = (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="space-y-4"
      >
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Partner&apos;s Email</FormLabel>
              <FormControl>
                <div className="relative">
                  <Mail className="text-muted-foreground absolute left-3 top-1/2 size-4 -translate-y-1/2" />
                  <Input
                    type="email"
                    placeholder="partner@example.com"
                    className="pl-10"
                    {...field}
                  />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          type="submit"
          className="w-full"
          disabled={!form.formState.isValid || isPending}
        >
          {isPending ? 'Sending...' : 'Send Invitation'}
        </Button>
      </form>
    </Form>
  );

  if (!isMobile) {
    return (
      <Dialog
        open={open}
        onOpenChange={handleOpenChange}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>
          {formContent}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer
      open={open}
      onOpenChange={handleOpenChange}
    >
      <DrawerContent className="max-h-[90vh] overflow-y-auto px-4 pb-6">
        <DrawerHeader className="text-left">
          <DrawerTitle>{title}</DrawerTitle>
          <DrawerDescription>{description}</DrawerDescription>
        </DrawerHeader>
        {formContent}
      </DrawerContent>
    </Drawer>
  );
}
